import { useEnrollmentStore } from '@/stores'
import {
  normalizeSelfEnrollmentEntryStep,
  SELF_ENROLLMENT_STEPS
} from '../../pages/selfEnrollmentFlow/utils/steps'
import {
  getEnrollmentBoolean,
  getEnrollmentNumber,
  getEnrollmentString,
  isNonEmptyString
} from './helpers'
import type { HydratableEnrollment } from './types'

export const hasSignedEnrollment = (enrollment: HydratableEnrollment) => {
  const status = getEnrollmentString(enrollment, ['signatureStatus', 'docuSignStatus']).toUpperCase()
  return (
    getEnrollmentBoolean(enrollment, ['enrollmentAgreementSigned']) === true ||
    status === 'SIGNED' ||
    status === 'COMPLETED'
  )
}

export const hasCompletedEnrollment = (enrollment: HydratableEnrollment) => {
  const status = getEnrollmentString(enrollment, ['status']).toUpperCase()
  return status === 'ENROLLED'
}

export const hasCompletedEnglishAssessment = (enrollment: HydratableEnrollment) => {
  const status = getEnrollmentString(enrollment, ['status']).toUpperCase()

  if (
    getEnrollmentBoolean(enrollment, ['englishTestCompleted']) === true ||
    getEnrollmentBoolean(enrollment, ['englishTestPassed']) === true ||
    isNonEmptyString(getEnrollmentString(enrollment, ['englishTestCompletedAt']))
  ) {
    return true
  }

  return new Set([
    'ASSESSMENT_PASSED',
    'DOCUMENTS_PENDING',
    'DOCUMENTS_UPLOADED',
    'SIGNING_PENDING',
    'ENROLLMENT_DOCS_SIGNED',
    'SUBMITTED',
    'APPROVED',
    'ENROLLED'
  ]).has(status)
}

export const normalizeCurrentStepForUi = (enrollment: HydratableEnrollment) => {
  const status = getEnrollmentString(enrollment, ['status']).toUpperCase()
  const currentStep = getEnrollmentNumber(enrollment, ['currentStep'])

  if (
    !hasCompletedEnglishAssessment(enrollment) &&
    (currentStep ?? 0) >= SELF_ENROLLMENT_STEPS.ENGLISH_PROFICIENCY
  ) {
    return SELF_ENROLLMENT_STEPS.ENGLISH_PROFICIENCY
  }

  if (status === 'ASSESSMENT_FAILED' || status === 'ASSESSMENT_PENDING') {
    return SELF_ENROLLMENT_STEPS.ENGLISH_PROFICIENCY
  }

  if (hasCompletedEnrollment(enrollment)) {
    return SELF_ENROLLMENT_STEPS.ENROLLMENT_COMPLETE
  }
  if (hasSignedEnrollment(enrollment)) {
    return SELF_ENROLLMENT_STEPS.SIGNED_COMPLETE
  }
  if (typeof currentStep === 'number') {
    const normalizedStep = normalizeSelfEnrollmentEntryStep(currentStep)

    if (status === 'DOCUMENTS_PENDING') {
      return Math.min(normalizedStep, SELF_ENROLLMENT_STEPS.UPLOAD_DOCUMENTS)
    }
    if (status === 'DOCUMENTS_UPLOADED' || status === 'SIGNING_PENDING') {
      return SELF_ENROLLMENT_STEPS.SIGN_DOCUMENTS
    }
    return normalizedStep
  }
  return currentStep
}

export const getMaxRestorableStepForStatus = (enrollment: HydratableEnrollment) => {
  const status = getEnrollmentString(enrollment, ['status']).toUpperCase()
  const localState = useEnrollmentStore.getState()

  if (!hasCompletedEnglishAssessment(enrollment)) {
    return SELF_ENROLLMENT_STEPS.ENGLISH_PROFICIENCY
  }

  if (
    localState.enrollmentCompleted
  ) {
    return SELF_ENROLLMENT_STEPS.ENROLLMENT_COMPLETE
  }

  if (hasCompletedEnrollment(enrollment)) {
    return SELF_ENROLLMENT_STEPS.ENROLLMENT_COMPLETE
  }

  if (hasSignedEnrollment(enrollment)) {
    return SELF_ENROLLMENT_STEPS.SIGNED_COMPLETE
  }

  switch (status) {
    case 'ASSESSMENT_FAILED':
    case 'ASSESSMENT_PENDING':
      return SELF_ENROLLMENT_STEPS.ENGLISH_PROFICIENCY
    case 'DOCUMENTS_PENDING':
      return SELF_ENROLLMENT_STEPS.UPLOAD_DOCUMENTS
    case 'DOCUMENTS_UPLOADED':
    case 'SIGNING_PENDING':
      return SELF_ENROLLMENT_STEPS.SIGN_DOCUMENTS
    case 'ENROLLMENT_DOCS_SIGNED':
      return SELF_ENROLLMENT_STEPS.SIGNED_COMPLETE
    default:
      return null
  }
}
